// BCM-898 · Goods Receipt Dashboard — Resubmit Document dialog, step 1 of 2.
//
// Same shape/behaviour as the Reset Batch dialog's first step
// (sasl/batchesDetails/ResetBatchDialog) — the batch ribbon (Batch Number / PO
// Number / CMO / Batch Status), the "Reason for Reset" dropdown and the
// "Allowed Batch Statuses" expander, with a "Continue" button.
//
// Per the Resubmit Document redesign (design image):
//   • Title is "Resubmit Document".
//   • Batch Number is PRE-POPULATED with the row the button was pressed on but is
//     STILL EDITABLE — a searchable dropdown over all FG batches. Picking another
//     batch re-derives PO Number / CMO / Batch Status and carries that batch into
//     step 2.
//   • The dropdown only offers the two document-resubmission reasons (the SLoC /
//     CCK / delivery-address change option is removed).
// On Continue this hands the chosen reason AND the selected batch to step 2
// (ResubmitDocumentDialog).
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { fetchAllFGBatches } from "../../api/goodsReceiptApi";

export const WITH_DIGI = "Document Resubmission with data extracted from digitalized document";
export const WITHOUT_DIGI = "Document Resubmission without digitalization of the document";

const RESUBMISSION_REASONS = [WITH_DIGI, WITHOUT_DIGI];

const ALLOWED_STATUSES = [
  {
    reason: `${WITH_DIGI}\n${WITHOUT_DIGI}`,
    allowedStatus: "All",
  },
];

const useStyles = makeStyles((theme) => ({
  // Dialog renders through a React portal (document.body), outside the
  // dashboard's own DOM subtree — pin the theme's font explicitly rather than
  // leaving it to inheritance, matching the Batch (SASL) dashboard's dialogs.
  dialogPaper: { borderRadius: 8, maxWidth: 900, maxHeight: "90vh", fontFamily: theme.typography.fontFamily },
  dialogTitle: { padding: theme.spacing(2, 3) },
  cancelButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    cursor: "pointer",
    color: "#000",
    fontSize: "1rem",
    fontWeight: 500,
    "&:hover": { textDecoration: "underline" },
  },
  titleText: {
    fontWeight: 600,
    fontSize: "1.5rem",
    color: "#000",
    textAlign: "center",
    width: "100%",
  },
  dialogContent: { padding: theme.spacing(2, 3) },
  fieldLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#000",
    display: "block",
  },
  requiredAsterisk: { color: "#FF0000", marginLeft: theme.spacing(0.25) },
  ribbonValue: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    color: "#000",
    fontSize: "0.875rem",
  },
  textField: {
    "& .MuiOutlinedInput-root": { height: 40, display: "flex", alignItems: "center" },
    "& .MuiSelect-select": { paddingLeft: 5 },
    "& .MuiAutocomplete-input": { padding: "8px 0px !important" },
    "& .MuiAutocomplete-inputRoot": { paddingTop: 0, paddingBottom: 0 },
  },
  expandableHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    cursor: "pointer",
  },
  expandableTitle: { fontWeight: 600, fontSize: "0.875rem", color: "#000" },
  tableContainer: { boxShadow: "none", border: "1px solid #E0E0E0" },
  tableHeader: { fontWeight: 600, color: "#000", backgroundColor: "#F5F5F5" },
  continueButton: {
    backgroundColor: "#e36e39",
    color: "#FFFFFF",
    borderRadius: 20,
    padding: "8px 32px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": { backgroundColor: "#c44d15" },
    "&:disabled": { backgroundColor: "#CCCCCC", color: "#666666" },
  },
  dialogActions: { padding: theme.spacing(2, 3, 4, 3), justifyContent: "center" },
}));

// The batch the button was launched from, as a stand-in option so the picker can
// show it (and the ribbon can render) before the full list finishes loading.
const initialBatchFrom = (source) => {
  if (!source.vendorBatchNumber) return null;
  return {
    vendorBatchNumber: source.vendorBatchNumber,
    scheduleAgreementNumber: source.scheduleAgreementNumber || "",
    vendorName: source.vendorName || source.cmoMember || "",
    batchStatusText: source.statusDisplayName || source.batchStatusText || "",
    schrockenDeliveryScheduleBatchID: source.schrockenDeliveryScheduleBatchID || "",
    vendorMosymphonyID: source.vendorMosymphonyID || "",
  };
};

const ResubmitReasonDialog = ({ open, onClose, row, detail, onContinue }) => {
  const classes = useStyles();
  const source = { ...(row || {}), ...(detail || {}) };

  const [expanded, setExpanded] = useState(false);
  const [reasonForReset, setReasonForReset] = useState("");
  const [batchesList, setBatchesList] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const cancelRef = useRef(null);
  useEffect(() => {
    return () => {
      if (cancelRef.current) cancelRef.current.cancel("leaving resubmit reason dialog");
    };
  }, []);

  // PO / CMO / Batch Status follow whichever batch is selected.
  const batchInfo = {
    poNumber: selectedBatch?.scheduleAgreementNumber || "",
    cmo: selectedBatch?.vendorName || selectedBatch?.cmoMember || "",
    batchStatus: selectedBatch?.batchStatusText || selectedBatch?.statusDisplayName || "",
  };

  // Pre-populate the picker with the launching batch each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setExpanded(false);
    setReasonForReset("");
    setSelectedBatch(initialBatchFrom(source));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load the full batch list so the pre-populated Batch Number stays editable.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    if (cancelRef.current) cancelRef.current.cancel("superseded batch-list fetch");
    const cancelSource = axios.CancelToken.source();
    cancelRef.current = cancelSource;
    setLoadingBatches(true);
    fetchAllFGBatches(cancelSource.token)
      .then((res) => {
        if (cancelled) return;
        const list =
          res.data && res.data.status && Array.isArray(res.data.batchesList)
            ? res.data.batchesList
            : [];
        setBatchesList(list);
        // Swap the stand-in option for the matching full batch object so the
        // identifiers carried into step 2 come from the authoritative record.
        if (list.length) {
          const match = list.find(
            (b) =>
              (source.schrockenDeliveryScheduleBatchID &&
                b.schrockenDeliveryScheduleBatchID ===
                  source.schrockenDeliveryScheduleBatchID) ||
              (source.vendorBatchNumber &&
                b.vendorBatchNumber === source.vendorBatchNumber)
          );
          if (match) setSelectedBatch(match);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!cancelled) setBatchesList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingBatches(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source.schrockenDeliveryScheduleBatchID, source.vendorBatchNumber]);

  const isFormValid = selectedBatch !== null && reasonForReset !== "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.cancelButton} onClick={onClose}>
          Cancel <CloseIcon style={{ fontSize: 24, marginLeft: 4, color: "#FF0000" }} />
        </Typography>
        <Typography className={classes.titleText}>Resubmit Document</Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* ── Ribbon — batch pre-populated from the row but still editable ─────── */}
        <Grid container spacing={3} style={{ marginTop: 8 }}>
          <Grid item xs={12} sm={3}>
            <Typography className={classes.fieldLabel}>
              Batch Number <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <Autocomplete
              options={batchesList}
              loading={loadingBatches}
              value={selectedBatch}
              getOptionLabel={(option) => option?.vendorBatchNumber || ""}
              getOptionSelected={(option, value) =>
                option?.schrockenDeliveryScheduleBatchID ===
                  value?.schrockenDeliveryScheduleBatchID ||
                option?.vendorBatchNumber === value?.vendorBatchNumber
              }
              onChange={(_, value) => {
                setSelectedBatch(value);
                setReasonForReset("");
              }}
              selectOnFocus={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={loadingBatches ? "Loading..." : "Select option"}
                  className={classes.textField}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon style={{ fontSize: 18, color: "#666", marginRight: 4 }} />
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography className={classes.fieldLabel}>PO Number</Typography>
            <div className={classes.ribbonValue}>{batchInfo.poNumber}</div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography className={classes.fieldLabel}>CMO</Typography>
            <div className={classes.ribbonValue}>{batchInfo.cmo}</div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography className={classes.fieldLabel}>Batch Status</Typography>
            <div className={classes.ribbonValue}>{batchInfo.batchStatus}</div>
          </Grid>
        </Grid>

        {/* ── Reason for Reset ─────────────────────────────────────────────────── */}
        <Grid container spacing={2} style={{ marginTop: 20 }}>
          <Grid item xs={12}>
            <Typography className={classes.fieldLabel}>
              Reason for Reset <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <FormControl fullWidth variant="outlined">
              <Select
                value={reasonForReset}
                onChange={(e) => setReasonForReset(e.target.value)}
                displayEmpty
                className={classes.textField}
                disabled={!selectedBatch}
              >
                <MenuItem value="" disabled>
                  {selectedBatch ? "Select from list" : "Please select a batch first"}
                </MenuItem>
                {RESUBMISSION_REASONS.map((reason, index) => (
                  <MenuItem key={index} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* ── Allowed batch statuses (same expander as Reset Batch) ─────────────── */}
        <div style={{ marginTop: 16 }}>
          <div className={classes.expandableHeader} onClick={() => setExpanded(!expanded)}>
            {expanded ? (
              <ExpandMoreIcon style={{ color: "#666", marginRight: 8 }} />
            ) : (
              <ChevronRightIcon style={{ color: "#666", marginRight: 8 }} />
            )}
            <Typography className={classes.expandableTitle}>
              Allowed Batch Statuses for each Reason for Reset
            </Typography>
          </div>
          <Collapse in={expanded}>
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader} style={{ width: "60%" }}>
                      Reason for Reset
                    </TableCell>
                    <TableCell className={classes.tableHeader} style={{ width: "40%" }}>
                      Allowed Batch Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ALLOWED_STATUSES.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell style={{ whiteSpace: "pre-line", width: "60%" }}>
                        {item.reason}
                      </TableCell>
                      <TableCell style={{ width: "40%" }}>{item.allowedStatus}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={() => onContinue(reasonForReset, selectedBatch)}
          className={classes.continueButton}
          disabled={!isFormValid}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResubmitReasonDialog;
